import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePinLock } from '../../contexts/PinLockContext';
import { formatCurrency } from '../../utils/formatters';
import { reportingDataService } from '../../services/reportingDataService';
import { appointmentService } from '../../services/appointmentService';
import { ymd } from '../../shared/dateUtils';
import {
    TrendingUp,
    Users,
    Calendar,
    DollarSign,
    Clock,
    Activity,
    Scissors,
    AlertCircle,
    RefreshCw
} from 'lucide-react';

interface DashboardStats {
    todayRevenue: number;
    todayAppointments: number;
    completedAppointments: number;
    pendingAppointments: number;
    newClientsToday: number;
    averageTicket: number;
    tips: number;
    noShows: number;
    walkIns: number;
}

interface TopService {
    name: string;
    count: number;
    revenue: number;
}

interface RecentActivity {
    type: 'booking' | 'payment' | 'client' | 'checkin';
    description: string;
    time: string;
    amount?: number;
}

const Dashboard: React.FC = () => {
    const { user } = useAuth();
    const { currentBarber } = usePinLock();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [topServices, setTopServices] = useState<TopService[]>([]);
    const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    // Get display name from either station login or user account
    const displayName = currentBarber?.barberName?.split(' ')[0] || user?.firstName || 'there';

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const today = ymd();

            // Get today's summary from reporting service
            const dailySummary = await reportingDataService.getDailySummary(today);

            // Today's REVENUE from the blended money spine (reconciled Fresha history + live SLIK, no
            // double-count) — same number the Register/Reports show, so the uploaded Fresha sales reflect
            // here too instead of $0. (Jose 2026-10-17: "the dashboard doesnt reflect either")
            const blendToday = await reportingDataService.getBlendedRevenue(today, today);

            // Get today's appointments for activity feed
            const todayAppointments = await appointmentService.getAppointmentsByDate(today);

            // Build stats (new clients count comes from dailySummary.clientActivity)
            setStats({
                todayRevenue: blendToday.total,
                todayAppointments: dailySummary.appointments.total,
                completedAppointments: dailySummary.appointments.completed,
                pendingAppointments: dailySummary.appointments.pending,
                newClientsToday: dailySummary.clientActivity.newClients || 0,
                averageTicket: blendToday.tickets > 0 ? Math.round(blendToday.total / blendToday.tickets) : dailySummary.metrics.averageTicket,
                tips: blendToday.tips || dailySummary.revenue.tips,
                noShows: dailySummary.appointments.noShows,
                walkIns: dailySummary.appointments.walkIns,
            });

            // Build top services from today's appointments
            const serviceMap = new Map<string, { count: number; revenue: number }>();
            todayAppointments
                .filter(apt => apt.status === 'completed' || apt.status === 'paid')
                .forEach(apt => {
                    const serviceName = apt.service || 'Unknown Service';
                    const existing = serviceMap.get(serviceName) || { count: 0, revenue: 0 };
                    serviceMap.set(serviceName, {
                        count: existing.count + 1,
                        revenue: existing.revenue + (apt.price || 0),
                    });
                });

            const services: TopService[] = Array.from(serviceMap.entries())
                .map(([name, data]) => ({ name, ...data }))
                .sort((a, b) => b.revenue - a.revenue)
                .slice(0, 5);
            setTopServices(services);

            // Build recent activity from today's appointments
            const activities: RecentActivity[] = todayAppointments
                .slice(0, 10)
                .map(apt => {
                    const time = apt.time || '';
                    const serviceName = apt.service || 'Service';

                    if (apt.status === 'completed' || apt.status === 'paid') {
                        return {
                            type: 'payment' as const,
                            description: `${apt.clientName} - ${serviceName}`,
                            time,
                            amount: apt.price || 0,
                        };
                    } else if (apt.status === 'checked-in' || apt.status === 'in-progress') {
                        return {
                            type: 'checkin' as const,
                            description: `${apt.clientName} checked in`,
                            time,
                        };
                    } else {
                        return {
                            type: 'booking' as const,
                            description: `${apt.clientName} - ${serviceName}`,
                            time,
                        };
                    }
                });
            setRecentActivity(activities);
            setLastUpdated(new Date());

        } catch (err: any) {
            console.error('Dashboard error:', err);
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDashboardData();

        // Refresh every 2 minutes
        const interval = setInterval(loadDashboardData, 120000);
        return () => clearInterval(interval);
    }, []);

    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error && !stats) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
                    <p className="mt-4 text-red-600 dark:text-red-400">{error}</p>
                    <button
                        onClick={loadDashboardData}
                        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const statCards = [
        {
            title: "Today's Revenue",
            value: formatCurrency(stats?.todayRevenue || 0),
            icon: DollarSign,
            color: 'green',
            subtext: `${stats?.completedAppointments || 0} completed`,
        },
        {
            title: 'Appointments',
            value: stats?.todayAppointments || 0,
            icon: Calendar,
            color: 'blue',
            subtext: `${stats?.pendingAppointments || 0} pending`,
        },
        {
            title: 'New Clients',
            value: stats?.newClientsToday || 0,
            icon: Users,
            color: 'purple',
            subtext: `${stats?.walkIns || 0} walk-ins`,
        },
        {
            title: 'Avg Ticket',
            value: formatCurrency(stats?.averageTicket || 0),
            icon: TrendingUp,
            color: 'orange',
            subtext: `Tips: ${formatCurrency(stats?.tips || 0)}`,
        }
    ];

    const getActivityIcon = (type: RecentActivity['type']) => {
        switch (type) {
            case 'payment': return <DollarSign className="w-4 h-4 text-green-500" />;
            case 'booking': return <Calendar className="w-4 h-4 text-blue-500" />;
            case 'client': return <Users className="w-4 h-4 text-purple-500" />;
            case 'checkin': return <Clock className="w-4 h-4 text-orange-500" />;
            default: return <Activity className="w-4 h-4 text-gray-500" />;
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                        Welcome back, {displayName}!
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        Here's what's happening today
                    </p>
                </div>
                <button
                    onClick={loadDashboardData}
                    disabled={loading}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    {loading ? 'Updating...' : 'Refresh'}
                </button>
            </div>

            {/* Last Updated */}
            {lastUpdated && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {statCards.map((stat, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-5 border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate">{stat.title}</p>
                                <p className="text-xl md:text-2xl font-bold mt-1 text-gray-900 dark:text-white">{stat.value}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stat.subtext}</p>
                            </div>
                            <stat.icon className={`w-8 h-8 md:w-10 md:h-10 flex-shrink-0 ${
                                stat.color === 'green' ? 'text-green-500' :
                                stat.color === 'blue' ? 'text-blue-500' :
                                stat.color === 'purple' ? 'text-purple-500' :
                                'text-orange-500'
                            }`} />
                        </div>
                    </div>
                ))}
            </div>

            {/* No-Shows Alert */}
            {(stats?.noShows || 0) > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div>
                        <p className="font-medium text-red-700 dark:text-red-400">
                            {stats?.noShows} no-show{stats?.noShows !== 1 ? 's' : ''} today
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-300">
                            Consider following up with these clients
                        </p>
                    </div>
                </div>
            )}

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Recent Activity */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 border border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-purple-500" />
                        Today's Activity
                    </h2>
                    {recentActivity.length > 0 ? (
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {recentActivity.map((activity, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    {getActivityIcon(activity.type)}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                            {activity.description}
                                        </p>
                                        {activity.amount !== undefined && (
                                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                +{formatCurrency(activity.amount)}
                                            </p>
                                        )}
                                    </div>
                                    <span className="text-xs text-gray-400 flex-shrink-0">{activity.time}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">No activity yet today</p>
                    )}
                </div>

                {/* Top Services */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6 border border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                        <Scissors className="w-5 h-5 text-purple-500" />
                        Top Services Today
                    </h2>
                    {topServices.length > 0 ? (
                        <div className="space-y-3">
                            {topServices.map((service, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <div className="flex items-center gap-3">
                                        <span className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full flex items-center justify-center text-xs font-bold">
                                            {idx + 1}
                                        </span>
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{service.name}</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {service.count} bookings
                                        </span>
                                        <p className="text-xs text-gray-400">
                                            {formatCurrency(service.revenue)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">No completed services yet today</p>
                    )}
                </div>
            </div>

            {/* Quick Stats Footer */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 md:p-6 text-white">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <p className="text-purple-200 text-xs">Completed</p>
                        <p className="text-2xl font-bold">{stats?.completedAppointments || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-purple-200 text-xs">Pending</p>
                        <p className="text-2xl font-bold">{stats?.pendingAppointments || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-purple-200 text-xs">Walk-ins</p>
                        <p className="text-2xl font-bold">{stats?.walkIns || 0}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-purple-200 text-xs">Tips</p>
                        <p className="text-2xl font-bold">{formatCurrency(stats?.tips || 0)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
